import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, Table, Thead, Tbody, Tr, Th, Td,
  Switch, HStack, VStack, Input, FormControl, FormLabel, useToast, Flex,
  Badge, IconButton, Tooltip, Spinner, Select, Checkbox,
} from '@chakra-ui/react';
import { FiPlus, FiDownload, FiToggleLeft, FiToggleRight, FiCalendar } from 'react-icons/fi';
import api from '../api';

const YEAR = new Date().getFullYear();

export default function NationalHolidaysModal({ isOpen, onClose }) {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filterYear, setFilterYear] = useState(String(YEAR));
  const [showForm, setShowForm] = useState(false);
  const [globalSettings, setGlobalSettings] = useState({
    count_against_allocation: false,
  });
  const [form, setForm] = useState({ name: '', date: '', show_in_schedule: true, mark_staff_unavailable: false, mark_resources_unavailable: false });
  const toast = useToast();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/national-holidays?year=${filterYear}`);
      setHolidays(data);
    } catch(e) {}
    setLoading(false);
  }, [filterYear]);

  useEffect(() => { if (isOpen) fetch(); }, [isOpen, fetch]);

  const toggleField = async (id, field, current) => {
    try {
      await api.put(`/national-holidays/${id}`, { [field]: !current });
      setHolidays(prev => prev.map(h => h.id === id ? { ...h, [field]: !current } : h));
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleAdd = async () => {
    if (!form.name || !form.date) { toast({ title: 'Name and date required', status: 'warning', duration: 2000 }); return; }
    try {
      await api.post('/national-holidays', form);
      toast({ title: 'Holiday added', status: 'success', duration: 1500 });
      setShowForm(false);
      setForm({ name: '', date: '', show_in_schedule: true, mark_staff_unavailable: false, mark_resources_unavailable: false });
      fetch();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post('/national-holidays/import-uk', { year: parseInt(filterYear) });
      toast({ title: data.message, status: 'success', duration: 3000 });
      fetch();
    } catch(e) { toast({ title: 'Import failed', status: 'error', duration: 2000 }); }
    setImporting(false);
  };

  const active = holidays.filter(h => h.is_active !== false);
  const silenced = holidays.filter(h => h.is_active === false);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader fontSize="md">
          <HStack>
            <FiCalendar />
            <Text>National & Bank Holidays</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Controls */}
          <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
            <HStack>
              <Text fontSize="11px" color="gray.600">Year:</Text>
              <Select size="xs" value={filterYear} onChange={e => setFilterYear(e.target.value)} w="80px">
                {[YEAR - 1, YEAR, YEAR + 1, YEAR + 2].map(y => <option key={y} value={y}>{y}</option>)}
              </Select>
            </HStack>
            <HStack>
              <Button size="xs" leftIcon={<FiDownload />} colorScheme="blue" variant="outline"
                onClick={handleImport} isLoading={importing}>
                Import UK Bank Holidays ({filterYear} & {parseInt(filterYear)+1})
              </Button>
              <Button size="xs" leftIcon={<FiPlus />} colorScheme="green" variant="outline"
                onClick={() => setShowForm(p => !p)}>
                {showForm ? 'Cancel' : 'Add Holiday'}
              </Button>
            </HStack>
          </Flex>

          {/* Global settings */}
          <Box bg="blue.50" p={3} borderRadius="md" mb={4} border="1px solid" borderColor="blue.200">
            <Text fontSize="11px" fontWeight="700" color="blue.700" mb={2}>Global Settings</Text>
            <HStack>
              <Switch size="sm" colorScheme="blue"
                isChecked={globalSettings.count_against_allocation}
                onChange={e => setGlobalSettings(p => ({ ...p, count_against_allocation: e.target.checked }))} />
              <Text fontSize="11px" color="blue.800">Count national holidays against staff annual leave allocation</Text>
            </HStack>
          </Box>

          {/* Add form */}
          {showForm && (
            <Box bg="green.50" p={3} borderRadius="md" mb={4} border="1px solid" borderColor="green.200">
              <Text fontSize="11px" fontWeight="700" color="green.700" mb={3}>Add Holiday</Text>
              <HStack spacing={3} flexWrap="wrap">
                <FormControl w="200px">
                  <FormLabel fontSize="10px" mb={0.5}>Name *</FormLabel>
                  <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                </FormControl>
                <FormControl w="150px">
                  <FormLabel fontSize="10px" mb={0.5}>Date *</FormLabel>
                  <Input size="sm" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
                </FormControl>
                <VStack align="flex-start" spacing={1}>
                  <Checkbox size="sm" isChecked={form.show_in_schedule} onChange={e => setForm(p => ({ ...p, show_in_schedule: e.target.checked }))}>
                    <Text fontSize="10px">Show in Schedule</Text>
                  </Checkbox>
                  <Checkbox size="sm" isChecked={form.mark_staff_unavailable} onChange={e => setForm(p => ({ ...p, mark_staff_unavailable: e.target.checked }))}>
                    <Text fontSize="10px">Mark Staff Unavailable</Text>
                  </Checkbox>
                  <Checkbox size="sm" isChecked={form.mark_resources_unavailable} onChange={e => setForm(p => ({ ...p, mark_resources_unavailable: e.target.checked }))}>
                    <Text fontSize="10px">Mark Resources Unavailable</Text>
                  </Checkbox>
                </VStack>
                <Button size="sm" colorScheme="green" onClick={handleAdd} alignSelf="flex-end">Add</Button>
              </HStack>
            </Box>
          )}

          {loading ? (
            <Flex justify="center" py={8}><Spinner /></Flex>
          ) : (
            <>
              {/* Active holidays */}
              <Text fontSize="11px" fontWeight="700" color="gray.600" mb={2}>
                Active — {filterYear} ({active.length})
              </Text>
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" mb={4} overflow="hidden">
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontSize="9px">Date</Th>
                      <Th fontSize="9px">Name</Th>
                      <Th fontSize="9px" textAlign="center">Schedule</Th>
                      <Th fontSize="9px" textAlign="center">Staff Unavailable</Th>
                      <Th fontSize="9px" textAlign="center">Resources Unavailable</Th>
                      <Th fontSize="9px" textAlign="center">Source</Th>
                      <Th fontSize="9px" textAlign="center">Silence</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {active.length === 0 && (
                      <Tr><Td colSpan={7} textAlign="center" py={4} color="gray.400" fontSize="11px">
                        No holidays for {filterYear}. Import UK bank holidays or add manually.
                      </Td></Tr>
                    )}
                    {active.map(h => (
                      <Tr key={h.id}>
                        <Td fontSize="11px">{new Date(h.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</Td>
                        <Td fontSize="11px" fontWeight="500">{h.name}</Td>
                        <Td textAlign="center">
                          <Switch size="sm" colorScheme="teal" isChecked={!!h.show_in_schedule}
                            onChange={() => toggleField(h.id, 'show_in_schedule', h.show_in_schedule)} />
                        </Td>
                        <Td textAlign="center">
                          <Switch size="sm" colorScheme="orange" isChecked={!!h.mark_staff_unavailable}
                            onChange={() => toggleField(h.id, 'mark_staff_unavailable', h.mark_staff_unavailable)} />
                        </Td>
                        <Td textAlign="center">
                          <Switch size="sm" colorScheme="red" isChecked={!!h.mark_resources_unavailable}
                            onChange={() => toggleField(h.id, 'mark_resources_unavailable', h.mark_resources_unavailable)} />
                        </Td>
                        <Td textAlign="center">
                          <Badge fontSize="7px" colorScheme={h.source === 'gov_api' ? 'blue' : 'gray'}>
                            {h.source === 'gov_api' ? 'GOV.UK' : 'Manual'}
                          </Badge>
                        </Td>
                        <Td textAlign="center">
                          <Tooltip label="Silence — listed but ignored by app">
                            <IconButton icon={<FiToggleRight size={14} />} size="xs" variant="ghost" colorScheme="gray"
                              onClick={() => toggleField(h.id, 'is_active', true)} aria-label="Silence" />
                          </Tooltip>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Silenced */}
              {silenced.length > 0 && (
                <>
                  <Text fontSize="11px" fontWeight="700" color="gray.400" mb={2}>
                    Silenced ({silenced.length}) — listed but not used by the app
                  </Text>
                  <Box border="1px solid" borderColor="gray.100" borderRadius="md" overflow="hidden">
                    <Table size="sm">
                      <Tbody>
                        {silenced.map(h => (
                          <Tr key={h.id} opacity={0.6}>
                            <Td fontSize="11px">{new Date(h.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</Td>
                            <Td fontSize="11px">{h.name}</Td>
                            <Td textAlign="right">
                              <Tooltip label="Make active">
                                <IconButton icon={<FiToggleLeft size={14} />} size="xs" variant="ghost" colorScheme="green"
                                  onClick={() => toggleField(h.id, 'is_active', false)} aria-label="Activate" />
                              </Tooltip>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
